/**
 * Date Range Picker Component
 *
 * Provides preset options (Last 7 Days, This Month, YTD, etc.)
 * plus custom date inputs for arbitrary ranges.
 */

import { useState, useEffect, useRef } from "react";

export interface DateRange {
  startDate: string;
  endDate: string;
  label: string;
}

type PresetKey =
  | "last7days"
  | "last30days"
  | "last90days"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "allTime"
  | "custom";

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Earliest date allowed (for all time calculation) */
  minDate?: string;
}

/**
 * Calculate preset date ranges
 */
function getPresetRanges(minDate?: string): Record<PresetKey, () => DateRange> {
  const today = new Date();
  const formatDate = (d: Date) => d.toISOString().split("T")[0];

  return {
    last7days: () => {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
        label: "Last 7 Days",
      };
    },
    last30days: () => {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
        label: "Last 30 Days",
      };
    },
    last90days: () => {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
        label: "Last 90 Days",
      };
    },
    thisMonth: () => {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
        label: "This Month",
      };
    },
    lastMonth: () => {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
        label: "Last Month",
      };
    },
    thisYear: () => {
      const start = new Date(today.getFullYear(), 0, 1);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
        label: "YTD",
      };
    },
    allTime: () => {
      return {
        startDate: minDate || "2025-01-01",
        endDate: formatDate(today),
        label: "All Time",
      };
    },
    custom: () => {
      // Returns current selection for custom
      return {
        startDate: "",
        endDate: "",
        label: "Custom",
      };
    },
  };
}

const PRESET_OPTIONS: { key: PresetKey; label: string }[] = [
  { key: "last7days", label: "7 Days" },
  { key: "last30days", label: "30 Days" },
  { key: "last90days", label: "90 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "thisYear", label: "YTD" },
  { key: "allTime", label: "All Time" },
  { key: "custom", label: "Custom" },
];

/**
 * Determine which preset matches the current date range
 */
function detectPreset(range: DateRange, minDate?: string): PresetKey {
  const presets = getPresetRanges(minDate);

  for (const opt of PRESET_OPTIONS) {
    if (opt.key === "custom") continue;
    const preset = presets[opt.key]();
    if (preset.startDate === range.startDate && preset.endDate === range.endDate) {
      return opt.key;
    }
  }
  return "custom";
}

export function DateRangePicker({ value, onChange, minDate }: DateRangePickerProps) {
  const [activePreset, setActivePreset] = useState<PresetKey>(() =>
    detectPreset(value, minDate)
  );
  const [showCustom, setShowCustom] = useState(activePreset === "custom");
  const [customStart, setCustomStart] = useState(value.startDate);
  const [customEnd, setCustomEnd] = useState(value.endDate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update custom fields when value changes externally
  useEffect(() => {
    setCustomStart(value.startDate);
    setCustomEnd(value.endDate);
  }, [value.startDate, value.endDate]);

  // Detect preset when value changes
  useEffect(() => {
    const detected = detectPreset(value, minDate);
    setActivePreset(detected);
    setShowCustom(detected === "custom");
  }, [value, minDate]);

  const handlePresetClick = (key: PresetKey) => {
    setActivePreset(key);

    if (key === "custom") {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const presets = getPresetRanges(minDate);
      const newRange = presets[key]();
      onChange(newRange);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onChange({
        startDate: customStart,
        endDate: customEnd,
        label: `${customStart} to ${customEnd}`,
      });
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }} ref={dropdownRef}>
      {/* Preset Buttons */}
      <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
        {PRESET_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handlePresetClick(opt.key)}
            style={{
              padding: "0.4rem 0.75rem",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
              fontSize: "0.8rem",
              backgroundColor:
                activePreset === opt.key ? "var(--accent-primary)" : "#374151",
              color: activePreset === opt.key ? "white" : "#94a3b8",
              fontWeight: activePreset === opt.key ? 600 : 400,
              transition: "all 0.15s ease",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Custom Date Inputs */}
      {showCustom && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginLeft: "0.5rem",
            padding: "0.25rem 0.5rem",
            backgroundColor: "#1e293b",
            borderRadius: "0.375rem",
            border: "1px solid #374151",
          }}
        >
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            style={{
              backgroundColor: "#0f172a",
              border: "1px solid #475569",
              borderRadius: "0.25rem",
              padding: "0.35rem 0.5rem",
              color: "#e2e8f0",
              fontSize: "0.8rem",
            }}
          />
          <span style={{ color: "#64748b", fontSize: "0.8rem" }}>to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            style={{
              backgroundColor: "#0f172a",
              border: "1px solid #475569",
              borderRadius: "0.25rem",
              padding: "0.35rem 0.5rem",
              color: "#e2e8f0",
              fontSize: "0.8rem",
            }}
          />
          <button
            onClick={handleCustomApply}
            disabled={!customStart || !customEnd}
            style={{
              padding: "0.35rem 0.75rem",
              borderRadius: "0.25rem",
              border: "none",
              cursor: customStart && customEnd ? "pointer" : "not-allowed",
              fontSize: "0.8rem",
              backgroundColor:
                customStart && customEnd ? "var(--accent-success)" : "#475569",
              color: "white",
              fontWeight: 500,
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
